/**
 * Direction classification (pipeline stage 6).
 *
 * Turns a smoothed head pose into one of the §6 direction buckets, using per-employee
 * thresholds where a calibration profile exists and organisation-wide ones otherwise.
 *
 * Three properties are deliberate:
 *
 * **Hysteresis.** Leaving `forward` requires crossing the threshold; returning requires
 * crossing back by an extra margin. Without it, someone whose natural resting pose sits near
 * the boundary generates a stream of alternating direction changes — and therefore a stream
 * of attention-away events — purely from sub-degree noise.
 *
 * **Dominant axis by normalised excess.** When a pose exceeds both the yaw and pitch bands,
 * the reported direction is whichever axis exceeded *its own* band by the larger proportion.
 * Comparing raw degrees would let a wide yaw band silently outvote a narrow pitch band.
 *
 * **Per-side yaw limits.** Real workstations are rarely centred on the camera, so the left
 * and right halves of the forward cone are independent.
 *
 * **Reasons, always.** Every decision carries the numbers behind it, so an attention-away
 * event can be explained to the person it concerns down to the threshold that triggered it.
 */

import type { ThresholdPolicy } from '../config.js';
import { offsetPose, type Direction, type FrameQuality, type HeadPose } from '../models.js';
import type { SmoothedPose } from '../vision/smoothing.js';

/**
 * Thresholds actually applied, after calibration.
 *
 * `source` is surfaced in evidence and in the admin UI so it is always clear whether a
 * finding rests on this employee's own calibration or on an organisational default.
 */
export interface EffectiveThresholds {
  readonly yawForwardMaxLeft: number;
  readonly yawForwardMaxRight: number;
  readonly yawStrongAway: number;
  readonly pitchUpMax: number;
  readonly pitchDownMax: number;
  readonly pitchStrongAway: number;
  readonly rollTolerance: number;
  readonly hysteresisDegrees: number;
  readonly neutral: HeadPose;
  readonly source: 'policy' | 'calibrated';
}

export function thresholdsFromPolicy(policy: ThresholdPolicy): EffectiveThresholds {
  return {
    yawForwardMaxLeft: policy.yawForwardMax,
    yawForwardMaxRight: policy.yawForwardMax,
    yawStrongAway: policy.yawStrongAway,
    pitchUpMax: policy.pitchUpMax,
    pitchDownMax: policy.pitchDownMax,
    pitchStrongAway: policy.pitchStrongAway,
    rollTolerance: policy.rollTolerance,
    hysteresisDegrees: policy.hysteresisDegrees,
    neutral: { yaw: 0, pitch: 0, roll: 0 },
    source: 'policy',
  };
}

/** Wider of the two yaw sides — for display and hysteresis clamping. */
export function widestYaw(thresholds: EffectiveThresholds): number {
  return Math.max(thresholds.yawForwardMaxLeft, thresholds.yawForwardMaxRight);
}

export interface DirectionDecision {
  readonly direction: Direction;
  readonly relativePose: HeadPose | null;
  readonly reasons: readonly string[];
  /**
   * Multiplier applied to pose confidence for pose-specific doubts — for example extreme
   * head roll, which distorts the landmark geometry the estimate rests on.
   */
  readonly confidenceFactor: number;
}

export function isAwayDirection(direction: Direction): boolean {
  return direction !== 'forward' && direction !== 'not_detected';
}

export class DirectionClassifier {
  private effective: EffectiveThresholds;
  private previous: Direction = 'not_detected';

  constructor(policy: ThresholdPolicy, calibrated?: EffectiveThresholds) {
    this.effective = calibrated ?? thresholdsFromPolicy(policy);
  }

  get effectiveThresholds(): EffectiveThresholds {
    return this.effective;
  }

  get previousDirection(): Direction {
    return this.previous;
  }

  /** Swap thresholds at runtime (recalibration, policy update) without losing hysteresis. */
  applyThresholds(thresholds: EffectiveThresholds): void {
    this.effective = thresholds;
  }

  reset(): void {
    this.previous = 'not_detected';
  }

  classify(smoothed: SmoothedPose | null, quality?: FrameQuality | null): DirectionDecision {
    if (smoothed === null) {
      this.previous = 'not_detected';
      const reason =
        quality != null && quality.flags.includes('no_face')
          ? 'no face visible to the camera'
          : 'no usable head pose in the smoothing window';
      return {
        direction: 'not_detected',
        relativePose: null,
        reasons: [reason],
        confidenceFactor: 0,
      };
    }

    const t = this.effective;
    const relative = offsetPose(smoothed.pose, t.neutral);
    const reasons: string[] = [];
    let confidenceFactor = 1;

    if (t.source === 'calibrated') {
      reasons.push(
        `thresholds calibrated for this workstation (neutral yaw ${fmt(t.neutral.yaw)}, ` +
          `pitch ${fmt(t.neutral.pitch)})`,
      );
    }

    if (Math.abs(relative.roll) > t.rollTolerance) {
      // Head tilt is not itself inattention — reading a document propped on the desk
      // produces it — but it does skew the landmark geometry the pose rests on.
      confidenceFactor *= 0.75;
      reasons.push(
        `head tilt ${fmt(relative.roll)} exceeds tolerance ${t.rollTolerance.toFixed(0)}deg; ` +
          'orientation is less reliable',
      );
    }

    const strong = strongAwayReason(relative, t);
    if (strong !== null) {
      reasons.push(strong);
      this.previous = 'strongly_away';
      return {
        direction: 'strongly_away',
        relativePose: relative,
        reasons,
        confidenceFactor,
      };
    }

    const returning = this.previous !== 'forward' && this.previous !== 'not_detected';
    const margin = returning ? t.hysteresisDegrees : 0;

    const yawLeftLimit = Math.max(0, t.yawForwardMaxLeft - margin);
    const yawRightLimit = Math.max(0, t.yawForwardMaxRight - margin);
    const pitchUpLimit = Math.max(0, t.pitchUpMax - margin);
    const pitchDownLimit = Math.max(0, t.pitchDownMax - margin);

    const yawLimit = relative.yaw < 0 ? yawLeftLimit : yawRightLimit;
    const yawExcess = Math.abs(relative.yaw) - yawLimit;
    const pitchLimit = relative.pitch > 0 ? pitchUpLimit : pitchDownLimit;
    const pitchExcess = Math.abs(relative.pitch) - pitchLimit;

    if (yawExcess <= 0 && pitchExcess <= 0) {
      if (returning) {
        reasons.push(
          `returned within the forward cone (hysteresis margin ${t.hysteresisDegrees.toFixed(0)}deg applied)`,
        );
      }
      reasons.push(
        `yaw ${fmt(relative.yaw)} within -${t.yawForwardMaxLeft.toFixed(0)}deg..` +
          `+${t.yawForwardMaxRight.toFixed(0)}deg and pitch ${fmt(relative.pitch)} within ` +
          `-${t.pitchDownMax.toFixed(0)}deg..+${t.pitchUpMax.toFixed(0)}deg`,
      );
      this.previous = 'forward';
      return { direction: 'forward', relativePose: relative, reasons, confidenceFactor };
    }

    // Compare how far each axis went past its own band, in proportion to that band.
    const yawRatio = yawExcess > 0 ? yawExcess / Math.max(yawLimit, 1e-6) : -1;
    const pitchRatio = pitchExcess > 0 ? pitchExcess / Math.max(pitchLimit, 1e-6) : -1;

    let direction: Direction;
    if (yawRatio >= pitchRatio) {
      direction = relative.yaw < 0 ? 'left' : 'right';
      const side = direction === 'left' ? 'left' : 'right';
      const limit = direction === 'left' ? t.yawForwardMaxLeft : t.yawForwardMaxRight;
      reasons.push(
        `yaw ${fmt(relative.yaw)} is beyond the ${limit.toFixed(0)}deg forward cone toward ` +
          `the employee's ${side}`,
      );
    } else {
      direction = relative.pitch > 0 ? 'up' : 'down';
      const limit = direction === 'up' ? t.pitchUpMax : t.pitchDownMax;
      reasons.push(
        `pitch ${fmt(relative.pitch)} is beyond the ` +
          `${direction === 'up' ? 'upward' : 'downward'} limit (${limit.toFixed(0)}deg)`,
      );
    }

    if (smoothed.stability < 0.5) {
      reasons.push(
        `head was moving during the window (scatter ${smoothed.yawMad.toFixed(1)}deg)`,
      );
    }

    this.previous = direction;
    return { direction, relativePose: relative, reasons, confidenceFactor };
  }
}

function strongAwayReason(relative: HeadPose, t: EffectiveThresholds): string | null {
  if (Math.abs(relative.yaw) >= t.yawStrongAway) {
    const side = relative.yaw < 0 ? 'left' : 'right';
    return (
      `yaw ${fmt(relative.yaw)} is at or beyond the strongly-away limit ` +
      `(${t.yawStrongAway.toFixed(0)}deg) toward the employee's ${side}`
    );
  }
  if (Math.abs(relative.pitch) >= t.pitchStrongAway) {
    return (
      `pitch ${fmt(relative.pitch)} is at or beyond the strongly-away limit ` +
      `(${t.pitchStrongAway.toFixed(0)}deg)`
    );
  }
  return null;
}

function fmt(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}deg`;
}
